import React, { useState, useEffect, useRef } from 'react';
import { Select, Toast, Button, Avatar } from '@douyinfe/semi-ui';
import { IconRefresh, IconUser } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const StaffSelector = ({ value, onChange, placeholder, disabled, single }) => {
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 100, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const searchTimeoutRef = useRef(null);
    // 内部状态用于控制选择器的显示值
    const [internalValue, setInternalValue] = useState([]);

    // 当外部value变化时，更新内部状态
    useEffect(() => {
        if (value) {
            const valueArray = Array.isArray(value) ? value : [value];
            const validValues = valueArray.filter(v => v && v !== '0');
            setInternalValue(validValues);

            // 检查是否需要获取员工详情
            const missingStaff = validValues.filter(uid => !staffList.some(staff => staff.uid === uid));
            if (missingStaff.length > 0) {
                fetchStaffDetails(missingStaff);
            }
        } else {
            setInternalValue([]);
        }
    }, [value]);

    const fetchStaffDetails = async (uids) => {
        try {
            const response = await request.get('/go/sp/o2o/listmini_staff_user/', {
                params: {
                    uids: uids.join(',')
                }
            });
            if (response.errno === 0 && response.list) {
                setStaffList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(staff => {
                        if (!newList.some(existingStaff => existingStaff.uid === staff.uid)) {
                            newList.push(staff);
                        }
                    });
                    return newList;
                });
            }
        } catch (error) {
            console.error('获取员工详情失败:', error);
        }
    };

    const fetchStaffList = async () => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/o2o/listmini_staff_user/', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    key: searchKey,
                    status: -1
                }
            });
            if (response.errno === 0) {
                setStaffList(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取教师列表失败');
            }
        } catch (error) {
            console.error('获取教师列表失败:', error);
            Toast.error('获取教师列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            fetchStaffList();
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchKey, pagination.page]);

    const handleChange = (selectedUids) => {
        // 更新内部状态，确保UI显示正确
        setInternalValue(selectedUids);
        // 调用外部onChange
        onChange?.(selectedUids);
    };

    const renderCustomOption = (staff) => {
        const optionStyle = {
            display: 'flex',
            paddingLeft: 24,
            paddingTop: 10,
            paddingBottom: 10,
        };
        return (
            <Select.Option
                key={staff.uid}
                value={staff.uid}
                style={optionStyle}
                showTick={true}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                        size="small"
                        src={staff.avatar}
                        style={{ flexShrink: 0 }}
                    >
                        {staff.name?.[0]}
                    </Avatar>
                    <div style={{ fontSize: 14 }}>{staff.name}</div>
                    {staff.title && (
                        <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>
                            ({staff.title})
                        </div>
                    )}
                </div>
            </Select.Option>
        );
    };

    const renderSelectedItem = (optionNode) => {
        const selectedStaff = staffList.find(staff => (staff.uid === optionNode.value) || (staff.uid === optionNode.value[0]));
        if (!selectedStaff) return optionNode.value;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar
                    size="small"
                    src={selectedStaff.avatar}
                    style={{ flexShrink: 0 }}
                >
                    {selectedStaff.name?.[0]}
                </Avatar>
                <span>{selectedStaff.name}</span>
                {selectedStaff.title && (
                    <span style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>
                        ({selectedStaff.title})
                    </span>
                )}
            </div>
        );
    };

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={{}}
            placeholder={placeholder || (single ? "请选择教师" : "请选择教师（多个用逗号分隔）")}
            filter
            searchPosition='dropdown'
            showClear
            multiple={!single}
            maxTagCount={single ? 1 : 3}
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconUser style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={disabled}
            renderSelectedItem={renderSelectedItem}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    setPagination(prev => ({ ...prev, page: 0 }));
                    fetchStaffList();
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                setPagination(prev => ({ ...prev, page: 0 }));
            }}
            outerBottomSlot={
                pagination.total > (pagination.page + 1) * pagination.limit && (
                    <Button
                        type="tertiary"
                        onClick={() => {
                            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                            fetchStaffList();
                        }}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        加载更多
                    </Button>
                )
            }
            outerTopSlot={
                <Button
                    icon={<IconRefresh />}
                    type="tertiary"
                    onClick={fetchStaffList}
                    style={{ width: '100%', marginBottom: '8px' }}
                >
                    刷新列表
                </Button>
            }
        >
            {staffList.map(staff => renderCustomOption(staff))}
        </Select>
    );
};

export default StaffSelector;